import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PaymentService} from '../../services/payment/payment.service';
import {AthleteService} from '../../services/athlete/athlete.service';
import {Subscription} from 'rxjs';
import Swal from 'sweetalert2';
import {environment} from '../../environments/environment';
import {legacyTestContractDemo} from './legacy-test-contract-demo';

interface PaymentAttempt {
    id: string;
    status: string;
    errorMessage: string | null;
    transactionId: string;
    historical: boolean;
    attemptedAt: string;
    completedAt: string | null;
    nextRetryAt: string | null;
}

interface Installment {
    number: number;
    amount: number;
    status: string;
    scheduledDate: string;
    paidAt: string | null;
    nextRetryAt: string | null;
    errorMessage: string | null;
    canReplaceCard?: boolean;
    attempts: PaymentAttempt[];
}

interface RecurringPaymentDetails {
    id: string;
    athleteName: string;
    categoryName: string;
    amount: number;
    paidAmount: number;
    status: string;
    paymentMethod: string;
    card?: {last4: string | null; expiry: string | null} | null;
    cardReplacementInProgress?: boolean;
    installments: Installment[];
}

export function installmentStatusLabel(status: string): string {
    switch (status) {
        case 'completed': return 'Pagata';
        case 'failed': return 'Non riuscita';
        case 'processing': return 'In elaborazione';
        case 'unknown': return 'Da verificare';
        default: return 'In attesa';
    }
}

const POS_SWAL_CLASSES = {
    popup: 'wisper-pos-swal',
    confirmButton: 'wisper-pos-swal__primary',
    cancelButton: 'wisper-pos-swal__ghost',
};

@Component({
    selector: 'app-recurring-payment-details',
    templateUrl: './recurring-payment-details.component.html',
    styleUrls: ['./recurring-payment-details.component.scss']
})
export class RecurringPaymentDetailsComponent implements OnInit, OnDestroy {
    details: RecurringPaymentDetails | null = null;
    loading = true;
    error = '';
    readonly backUrl: string;
    readonly isDemo: boolean;
    private posStatusSubscription: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private paymentService: PaymentService,
        private athleteService: AthleteService,
    ) {
        this.backUrl = router.url.startsWith('/admins') ? '/admins/dashboard' : '/companies/dashboard';
        this.isDemo = !environment.production && !!route.snapshot.data['demo'];
    }

    ngOnInit(): void {
        void this.loadDetails();
    }

    ngOnDestroy(): void {
        this.posStatusSubscription?.unsubscribe();
    }

    async loadDetails(): Promise<void> {
        if (this.isDemo) {
            this.details = legacyTestContractDemo;
            this.loading = false;
            return;
        }

        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.error = 'Pagamento non trovato.';
            this.loading = false;
            return;
        }

        this.loading = true;
        this.error = '';
        try {
            const response = await this.paymentService.getRecurringPaymentDetails(id);
            if (!response?.success || !response.data) {
                this.error = response?.error || 'Dettaglio delle rate non disponibile.';
                return;
            }
            this.details = response.data;
        } catch (error: any) {
            this.error = error?.error?.error || 'Impossibile caricare il dettaglio delle rate.';
        } finally {
            this.loading = false;
        }
    }

    statusLabel(status: string): string {
        return installmentStatusLabel(status);
    }

    // The POS charges the failed installment on the new card and tokenizes it; the API then
    // moves the remaining installments to that card.
    async replaceCard(installment: Installment): Promise<void> {
        const paymentId = this.details?.id;
        if (!paymentId) return;

        const amount = installment.amount.toLocaleString('it-IT', {style: 'currency', currency: 'EUR'});
        const confirmation = await Swal.fire({
            icon: 'question',
            title: `Pagare la rata ${installment.number} con un'altra carta?`,
            html: `Il POS addebiterà <b>${amount}</b> sulla nuova carta dell'atleta.<br>` +
                'Le rate successive verranno addebitate su questa carta.',
            showCancelButton: true,
            confirmButtonText: 'Invia al POS',
            cancelButtonText: 'Annulla',
            reverseButtons: true,
            customClass: POS_SWAL_CLASSES,
        });
        if (!confirmation.isConfirmed) return;

        try {
            await this.paymentService.replaceRecurringCard(paymentId, installment.number);
        } catch (error: any) {
            await Swal.fire({
                icon: 'error',
                title: 'Pagamento non avviato',
                text: error?.error?.error || 'Impossibile inviare il pagamento al POS.',
                customClass: POS_SWAL_CLASSES,
            });
            return;
        }

        void Swal.fire({
            title: 'Pagamento inviato al POS',
            text: "Chiedi all'atleta di avvicinare la nuova carta al POS.",
            allowOutsideClick: false,
            showConfirmButton: false,
            showCloseButton: true,
            customClass: POS_SWAL_CLASSES,
            didOpen: () => Swal.showLoading(),
        }).then((result) => {
            if (result.dismiss === Swal.DismissReason.close) {
                this.posStatusSubscription?.unsubscribe();
                void this.loadDetails();
            }
        });

        this.posStatusSubscription?.unsubscribe();
        this.posStatusSubscription = this.athleteService.listenStatusPaymentPos(paymentId).subscribe({
            next: (update: any) => {
                if (update?.status === 'PROCESSED' || update?.status === 'CANCELED') {
                    void this.finishCardReplacement(installment.number, update.status);
                }
            },
            error: () => void this.finishCardReplacement(installment.number, 'ERROR'),
        });
    }

    private async finishCardReplacement(installmentNumber: number, posStatus: string): Promise<void> {
        this.posStatusSubscription?.unsubscribe();
        this.posStatusSubscription = null;
        // PROCESSED is stored before the webhook registers the new contract and settles the installment.
        if (posStatus === 'PROCESSED') {
            await new Promise((resolve) => setTimeout(resolve, 3000));
        }
        await this.loadDetails();

        const paid = this.details?.installments.find((item) => item.number === installmentNumber)?.status === 'completed';
        if (paid) {
            await Swal.fire({
                icon: 'success',
                title: `Rata ${installmentNumber} pagata`,
                text: 'Le prossime rate verranno addebitate sulla nuova carta.',
                customClass: POS_SWAL_CLASSES,
            });
        } else if (posStatus === 'CANCELED') {
            await Swal.fire({
                icon: 'info',
                title: 'Pagamento annullato',
                text: 'La rata non è stata pagata e la carta non è cambiata.',
                customClass: POS_SWAL_CLASSES,
            });
        } else {
            await Swal.fire({
                icon: 'warning',
                title: 'Esito non ancora disponibile',
                text: 'Controlla lo stato della rata tra qualche minuto prima di riprovare.',
                customClass: POS_SWAL_CLASSES,
            });
        }
    }

    paidInstallments(): number {
        return this.details?.installments.filter((item) => item.status === 'completed').length ?? 0;
    }
}
